/**
 * FirstExperience — 4-step onboarding to create the user's Organization.
 * Uses the existing `POST /api/organizations` endpoint (idempotent).
 *
 * Flow:
 *   Step 1 — Company Info      (name, country)
 *   Step 2 — Industry & Goals  (industry, goal)
 *   Step 3 — Reporting Std     (standards[])
 *   Step 4 — Launch summary
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Building2, Target, ShieldCheck, Rocket, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';

const STEPS = [
  { id: 'company',    title: 'Company',    icon: Building2 },
  { id: 'industry',   title: 'Industry',   icon: Target },
  { id: 'standards',  title: 'Standards',  icon: ShieldCheck },
  { id: 'finish',     title: 'Launch',     icon: Rocket },
];

const INDUSTRIES = ['Steel', 'Automotive', 'Chemical', 'Manufacturing', 'Cement', 'Aluminum', 'Textile', 'Electronics', 'Food & Beverage', 'Other'];
const COUNTRIES = ['Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Poland', 'UK', 'USA', 'UAE', 'Saudi Arabia', 'Turkey', 'India', 'China', 'Other'];
const GOALS = ['PCF Calculation', 'CBAM Compliance', 'Audit Preparation', 'Supplier Data Collection', 'Sustainability Reporting', 'Other'];
const STANDARDS = ['GHG Protocol', 'ISO 14067', 'ISO 14064', 'CBAM', 'CSRD', 'EU ETS', 'Saudi GGM'];

export default function FirstExperience() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setOrganization, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    country: '',
    industry: '',
    employees: 0,
    goal: 'PCF Calculation',
    standards: ['GHG Protocol'],
  });

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const toggleStandard = (std) => setForm((prev) => ({
    ...prev,
    standards: prev.standards.includes(std)
      ? prev.standards.filter((s) => s !== std)
      : [...prev.standards, std],
  }));

  const canProceed = () => {
    if (step === 0) return form.name.trim().length >= 2 && !!form.country;
    if (step === 1) return !!form.industry && !!form.goal;
    if (step === 2) return form.standards.length > 0;
    return true;
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await api.post('/organizations', form);
      const org = res.data?.organization;
      if (org) setOrganization(org);
      if (typeof refreshUser === 'function') await refreshUser();
      toast.success('Workspace ready');
      navigate('/workspace', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create workspace');
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    if (step === STEPS.length - 1) return handleSubmit();
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 py-10" data-testid="first-experience-page">
      <div className="w-full max-w-2xl">
        <div className="flex items-center justify-center mb-6">
          <Logo size={44} variant="full" tagline />
        </div>

        <Card className="p-6 sm:p-8 shadow-lg border-border" data-testid="first-experience-card">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-8">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const active = idx === step;
              const done = idx < step;
              return (
                <div key={s.id} className="flex-1 flex flex-col items-center" data-testid={`fe-step-indicator-${idx}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                    done ? 'bg-emerald-500 text-white'
                    : active ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className={`text-[10px] mt-1.5 uppercase tracking-wide ${active ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                    {s.title}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Step content */}
          {step === 0 && (
            <div className="space-y-4" data-testid="fe-step-company">
              <div>
                <h2 className="text-xl font-heading font-semibold">Set up your company</h2>
                <p className="text-sm text-muted-foreground mt-1">We&apos;ll tailor your workspace based on this.</p>
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="fe-name">Company name *</Label>
                  <Input id="fe-name" data-testid="fe-input-name"
                         placeholder="Acme Steel GmbH" value={form.name}
                         onChange={(e) => update('name', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="fe-country">Country *</Label>
                  <select id="fe-country" data-testid="fe-input-country"
                          value={form.country} onChange={(e) => update('country', e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    <option value="">Select country…</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="fe-emp">Employees (optional)</Label>
                  <Input id="fe-emp" data-testid="fe-input-employees" type="number" min="0"
                         placeholder="e.g. 250"
                         value={form.employees || ''}
                         onChange={(e) => update('employees', parseInt(e.target.value || '0', 10))} />
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="fe-step-industry">
              <div>
                <h2 className="text-xl font-heading font-semibold">Industry & primary goal</h2>
                <p className="text-sm text-muted-foreground mt-1">Drives which benchmarks and emission factors we surface.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fe-industry">Industry *</Label>
                <select id="fe-industry" data-testid="fe-input-industry"
                        value={form.industry} onChange={(e) => update('industry', e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="">Select industry…</option>
                  {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Primary goal *</Label>
                <div className="flex flex-wrap gap-2 pt-1">
                  {GOALS.map((g) => (
                    <button key={g} type="button"
                            data-testid={`fe-goal-${g.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            onClick={() => update('goal', g)}
                            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                              form.goal === g
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                            }`}>
                      {g}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="fe-step-standards">
              <div>
                <h2 className="text-xl font-heading font-semibold">Reporting standards</h2>
                <p className="text-sm text-muted-foreground mt-1">Select the frameworks your team reports against.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {STANDARDS.map((s) => {
                  const active = form.standards.includes(s);
                  return (
                    <button key={s} type="button"
                            data-testid={`fe-std-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            onClick={() => toggleStandard(s)}
                            className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                              active
                                ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/50'
                                : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                            }`}>
                      {s}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground">You can change these anytime from Settings.</p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 text-center py-4" data-testid="fe-step-finish">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Rocket className="w-7 h-7 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-semibold">Your workspace is ready</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  <span className="font-medium text-foreground">{form.name || 'Your company'}</span>
                  {' · '}{form.industry || '—'}{' · '}{form.country || '—'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {form.standards.map((s) => (
                  <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">
                We&apos;ll now generate a personalized dashboard for the &ldquo;{form.goal}&rdquo; goal.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <Button variant="ghost" onClick={back} disabled={step === 0 || saving} data-testid="fe-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="text-xs text-muted-foreground" data-testid="fe-step-counter">Step {step + 1} of {STEPS.length}</div>
            <Button onClick={next} disabled={!canProceed() || saving} data-testid="fe-next-btn">
              {saving
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creating…</>
                : step === STEPS.length - 1
                  ? <>Launch workspace <Rocket className="w-4 h-4 ml-1" /></>
                  : <>Continue <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-4">
          Your data stays private to your organization · GDPR compliant · EU-hosted
        </p>
      </div>
    </div>
  );
}
